import { fromJS } from "immutable";
import {
  isFinalStatus,
  jobFromPusher,
  mergeBulkJob,
} from "pages/Rostering/UpdatedTimeSheet/bulkAnalyse/model/bulkAnalyseModel";
import BULK_ANALYSE_ACTIONS from "../actions/bulkAnalyse.actions";

const buildInitialState = () =>
  fromJS({
    job: null,
    activeLoaded: false,
    preview: null,
    previewLoading: false,
    previewError: "",
    createLoading: false,
    createError: null,
    stopLoading: false,
    stopError: "",
    retryLoading: false,
    dismissLoading: false,
    items: [],
    itemsPagination: { current_page: 1, last_page: 1, total: 0, per_page: 10 },
    itemsStatus: "all",
    itemsLoading: false,
    jobOpen: false,
    // Bumps when a run reaches a final state so the timesheet table reloads
    // and shows the new reports.
    finishedVersion: 0,
  });

/** Version-guarded run update; bumps finishedVersion on running → final. */
const applyJob = (state, incoming) => {
  const current = state.get("job")?.toJS?.() || null;
  const next = mergeBulkJob(current, incoming);
  if (next === current) return state;

  let updated = state.set("job", next ? fromJS(next) : null);
  const becameFinal =
    next &&
    isFinalStatus(next.status) &&
    (!current || current.uuid !== next.uuid || !isFinalStatus(current.status));
  if (becameFinal && current) {
    updated = updated.set("finishedVersion", state.get("finishedVersion") + 1);
  }
  return updated;
};

export default function bulkAnalyseReducer(state = buildInitialState(), action) {
  switch (action.type) {
    case BULK_ANALYSE_ACTIONS.ACTIVE.SUCCESS: {
      const job = action.data || null;
      const withLoaded = state.set("activeLoaded", true);
      if (!job) {
        return withLoaded.set("job", null).set("jobOpen", false);
      }
      return applyJob(withLoaded, job);
    }
    case BULK_ANALYSE_ACTIONS.ACTIVE.ERROR:
      return state.set("activeLoaded", true);

    case BULK_ANALYSE_ACTIONS.PREVIEW.PENDING:
      return state.set("previewLoading", true).set("previewError", "");
    case BULK_ANALYSE_ACTIONS.PREVIEW.SUCCESS:
      return state.set("previewLoading", false).set("preview", fromJS(action.data || null));
    case BULK_ANALYSE_ACTIONS.PREVIEW.ERROR:
      return state.set("previewLoading", false).set("previewError", action.error || "");

    case BULK_ANALYSE_ACTIONS.CREATE.PENDING:
      return state.set("createLoading", true).set("createError", null);
    case BULK_ANALYSE_ACTIONS.CREATE.SUCCESS:
      return applyJob(state.set("createLoading", false), action.data)
        .set("items", fromJS([]))
        .set("itemsStatus", "all");
    case BULK_ANALYSE_ACTIONS.CREATE.ERROR:
      return state.set("createLoading", false).set("createError", fromJS(action.error || null));
    case BULK_ANALYSE_ACTIONS.CLEAR_CREATE_ERROR:
      return state.set("createError", null);

    case BULK_ANALYSE_ACTIONS.DETAIL.PENDING:
      return state.set("itemsLoading", true);
    case BULK_ANALYSE_ACTIONS.DETAIL.SUCCESS: {
      const data = action.data || {};
      const withJob = data.job ? applyJob(state, data.job) : state;
      return withJob
        .set("itemsLoading", false)
        .set("items", fromJS(data.items || []))
        .set("itemsPagination", fromJS(data.pagination || {}))
        .set("itemsStatus", data.status || state.get("itemsStatus"));
    }
    case BULK_ANALYSE_ACTIONS.DETAIL.ERROR:
      return state.set("itemsLoading", false);

    case BULK_ANALYSE_ACTIONS.STOP.PENDING:
      return state.set("stopLoading", true).set("stopError", "");
    case BULK_ANALYSE_ACTIONS.STOP.SUCCESS:
      return applyJob(state.set("stopLoading", false).set("stopError", ""), action.data);
    case BULK_ANALYSE_ACTIONS.STOP.ERROR:
      return state.set("stopLoading", false).set("stopError", action.error || "");

    case BULK_ANALYSE_ACTIONS.RETRY.PENDING:
      return state.set("retryLoading", true);
    case BULK_ANALYSE_ACTIONS.RETRY.SUCCESS:
      return applyJob(state.set("retryLoading", false), action.data).set("itemsStatus", "all");
    case BULK_ANALYSE_ACTIONS.RETRY.ERROR:
      return state.set("retryLoading", false);

    case BULK_ANALYSE_ACTIONS.DISMISS.PENDING:
      return state.set("dismissLoading", true);
    case BULK_ANALYSE_ACTIONS.DISMISS.SUCCESS:
      return applyJob(state.set("dismissLoading", false), action.data);
    case BULK_ANALYSE_ACTIONS.DISMISS.ERROR:
      return state.set("dismissLoading", false);

    case BULK_ANALYSE_ACTIONS.PUSHER_RECEIVED: {
      const current = state.get("job")?.toJS?.() || null;
      const partial = jobFromPusher(action.data, current);
      // A different run (another manager started one) is loaded in full by the saga.
      if (!partial || !current || current.uuid !== partial.uuid) return state;
      return applyJob(state, partial);
    }

    case BULK_ANALYSE_ACTIONS.SET_JOB_OPEN:
      return state.set("jobOpen", action.open);

    case BULK_ANALYSE_ACTIONS.RESET:
      return buildInitialState();

    default:
      return state;
  }
}
